import { FormHelperText, styled, Typography } from '@mui/material';
import { Option } from '@api/types/option';
import { DefaultTFuncReturn } from 'i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Gap } from '@components/layout-util-components/gap';
import Column from '@components/layout-util-components/column';

interface Props<T> {
  name: string;
  label?: string | DefaultTFuncReturn;
  error?: string;
  options: Array<Option<T>>;
  value: T;
  onChange: (value: T) => void;
  compact?: boolean;
  disabled?: boolean;
}

const StyledContainer = styled('div')<{ compact?: boolean }>(({ compact }) => ({
  display: 'flex',
  flexDirection: 'row',
  height: compact ? 42 : 58,
  alignItems: 'stretch',
  gap: compact ? 10 : 30,
}));

const StyledOption = styled('div')<{
  isSelected: boolean;
  compact?: boolean;
  disabled?: boolean;
}>(({ isSelected, compact, disabled }) => ({
  display: 'inline-flex',
  maxWidth: compact ? 176 : 275,
  flexGrow: 1,
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #00000040',
  borderRadius: 4,
  backgroundColor: 'white',
  padding: compact ? 8 : 16,
  cursor: disabled ? 'default' : 'pointer',
  ...(isSelected && {
    border: '2px solid rgba(255, 108, 0, 1)',
  }),

  'input[type="radio"]': {
    appearance: 'none',
    margin: 0,
  },
}));

export default function BinarySelectField<T>({
  name,
  options,
  label,
  error,
  onChange,
  value,
  compact,
  disabled,
}: Props<T>) {
  if (options.length !== 2) {
    throw new Error('binary select options must always be a length of 2');
  }

  const debounceOnChange = useDebouncedCallback(onChange);

  return (
    <Column>
      {label ? (
        <>
          <Typography>{label}</Typography>
          <Gap size={15} />
        </>
      ) : null}
      <StyledContainer compact={compact}>
        {options.map(({ id, label }, index) => {
          const elementId = `${name}-${id}-option`;
          const isSelected = id === value;

          return (
            <StyledOption
              key={`${id}`}
              isSelected={isSelected}
              disabled={disabled}
              onClick={(e) => {
                if (disabled) {
                  e.stopPropagation();
                  return;
                }
                debounceOnChange(id);
              }}
              compact={compact}
            >
              <label htmlFor={elementId}>
                <input
                  type="radio"
                  id={elementId}
                  name={name}
                  value={`${label}`}
                  disabled={disabled}
                />
                {label}
              </label>
            </StyledOption>
          );
        })}
      </StyledContainer>
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
    </Column>
  );
}
